import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, Truck, Award, Users,
  ChevronLeft, ChevronRight, CheckCircle,
  MapPin, Package, TrendingUp, Heart, ExternalLink, Sparkles
} from 'lucide-react';
import MedicineScene from '../components/MedicineScene';
import { api } from '../api';

import AlumVelumBanner from '../assets/image/Home_banner/Alum velum 1.png';
import DearaBanner from '../assets/image/Home_banner/Deara 4.png';
import GoldSoapBanner from '../assets/image/Home_banner/Gold soap 1.png';
import MoixyBanner from '../assets/image/Home_banner/Moixy 1.png';
import VitaleenBanner from '../assets/image/Home_banner/Vitaleen.png';
import PharmaceuticalsBanner from '../assets/image/Home_banner/Pharma.png';


const TEAL = '#2eb4b4';
const NAVY = '#174f9a';

const bannerSlides = [
  {
    image: PharmaceuticalsBanner,
    tag: 'Pharmaceuticals',
    title: 'Pharmaceuticals',
    highlight: 'Quality Healthcare Solutions',
    desc: 'Explore our comprehensive range of pharmaceutical formulations designed to meet modern healthcare needs.',
    cta: 'View Products',
    ctaPath: '/products?chemical_category=17',
    accent: '#2563eb'
  },
  {
    image: AlumVelumBanner,
    tag: 'Oral Care',
    title: 'Alum Velum',
    highlight: 'Premium Tooth Care',
    desc: 'Premium tooth powder and paste formulated for daily oral hygiene with natural ingredients.',
    cta: 'View Product',
    ctaPath: '/products/98',
    accent: '#2eb4b4'
  },
  {
    image: DearaBanner,
    tag: 'Baby Care',
    title: 'Deara',
    highlight: 'Gentle Baby Range',
    desc: 'Specially formulated baby care products for the gentlest, safest daily care routine.',
    cta: 'View Product',
    ctaPath: '/products/113',
    accent: '#e879a0'
  },
  {
    image: GoldSoapBanner,
    tag: 'Cosmetics',
    title: 'Gold Soap',
    highlight: 'Loveleen Collection',
    desc: 'Premium cosmetic products formulated for gentle, effective daily skincare routines.',
    cta: 'View Product',
    ctaPath: '/products/102',
    accent: '#f59e0b'
  },
  {
    image: MoixyBanner,
    tag: 'Cosmetics',
    title: 'Moixy',
    highlight: 'Premium Soap',
    desc: 'Luxury soap crafted with skin-loving ingredients for a refreshing daily experience.',
    cta: 'View Product',
    ctaPath: '/products/112',
    accent: '#7c3aed'
  },
  {
    image: VitaleenBanner,
    tag: 'Nutraceuticals',
    title: 'Vitaleen',
    highlight: 'Health Supplements',
    desc: 'Essential vitamins and health supplements for holistic well-being of the entire family.',
    cta: 'View Product',
    ctaPath: '/products/2',
    accent: '#059669'
  },
];

const stats = [
  { value: '500+',  label: 'Products',         icon: Package   },
  { value: '15+',   label: 'Years Experience', icon: Award     },
  { value: '1000+', label: 'Partners',         icon: Users     },
  { value: '20+',   label: 'District Coverage',  icon: MapPin    },
];

const whyUs = [
  { icon: Shield,     title: 'Quality Assurance',     desc: 'All products sourced from WHO-GMP certified manufacturers with rigorous quality checks.'  },
  { icon: Truck,      title: 'Reliable Distribution', desc: 'Extensive distribution network ensuring timely delivery across urban and rural areas.'     },
  { icon: Award,      title: 'Certified Standards',   desc: 'Strict adherence to pharmaceutical regulations, GST compliance, and legal documentation.' },
  { icon: Heart,      title: 'Patient-First',         desc: 'Every decision is guided by patient safety, efficacy, and affordability.'                  },
  { icon: TrendingUp, title: 'Market Growth',         desc: 'Strategic marketing and brand development to drive growth for our partners.'               },
  { icon: Users,      title: 'Strong Network',        desc: 'Long-term relationships with hospitals, pharmacies, and distributors across India.'         },
];

const featuredProductStyles = [
  { color: 'from-teal-400 to-teal-700',    accent: '#2eb4b4', tagBg: 'rgba(46,180,180,0.12)',  tagColor: '#2eb4b4' },
  { color: 'from-blue-400 to-blue-700',    accent: '#174f9a', tagBg: 'rgba(23,79,154,0.12)',   tagColor: '#174f9a' },
  { color: 'from-amber-400 to-orange-500', accent: '#d97706', tagBg: 'rgba(217,119,6,0.12)',   tagColor: '#b45309' },
  { color: 'from-green-400 to-teal-500',   accent: '#059669', tagBg: 'rgba(5,150,105,0.12)',   tagColor: '#047857' },
  { color: 'from-purple-400 to-blue-500',  accent: '#7c3aed', tagBg: 'rgba(124,58,237,0.12)',  tagColor: '#6d28d9' },
  { color: 'from-pink-400 to-rose-500',    accent: '#e879a0', tagBg: 'rgba(232,121,160,0.12)', tagColor: '#db2777' },
];

/* ─── Hooks ─── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

function CountUp({ end, duration = 2000, trigger }) {
  const [count, setCount] = useState(0);
  const numEnd = parseInt(end);
  useEffect(() => {
    if (!trigger || isNaN(numEnd)) return;
    let val = 0;
    const step  = numEnd / (duration / 16);
    const timer = setInterval(() => {
      val = Math.min(val + step, numEnd);
      setCount(Math.floor(val));
      if (val >= numEnd) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, numEnd, duration]);
  return <>{isNaN(numEnd) ? end : count + end.replace(/\d+/, '')}</>;
}

function AnimatedBar({ pct, trigger, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const t = setTimeout(() => setWidth(pct), delay);
    return () => clearTimeout(t);
  }, [trigger, pct, delay]);
  return (
    <div style={{ height: 8, background: '#e5eaf3', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${width}%`,
        background: `linear-gradient(to right, ${TEAL}, ${NAVY})`,
        borderRadius: 99, transition: `width 1.1s cubic-bezier(.22,1,.36,1)`,
      }} />
    </div>
  );
}

const grad  = { background: `linear-gradient(135deg, ${TEAL}, ${NAVY})` };
const gradT = { background: `linear-gradient(to right, ${TEAL}, ${NAVY})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };
const btnTeal = {
  ...grad, color: '#fff', fontWeight: 700, padding: '14px 28px', borderRadius: 14,
  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15,
  boxShadow: `0 8px 30px rgba(46,180,180,0.30)`, transition: 'all .25s',
  textDecoration: 'none', border: 'none', cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function Reveal({ children, delay = 0, direction = 'up', style: extraStyle = {} }) {
  const [ref, vis] = useInView(0.1);
  const fromY = direction === 'up' ? 32 : direction === 'down' ? -32 : 0;
  const fromX = direction === 'left' ? 32 : direction === 'right' ? -32 : 0;
  return (
    <div ref={ref} style={{
      transition: `opacity 0.9s cubic-bezier(.19,1,.22,1) ${delay}ms, transform 0.9s cubic-bezier(.19,1,.22,1) ${delay}ms, filter 0.9s cubic-bezier(.19,1,.22,1) ${delay}ms`,
      opacity:   vis ? 1 : 0,
      transform: vis ? 'translate3d(0,0,0)' : `translate3d(${fromX}px,${fromY}px,0)`,
      filter:    vis ? 'blur(0)' : 'blur(5px)',
      willChange: 'opacity, transform, filter',
      ...extraStyle,
    }}>
      {children}
    </div>
  );
}

/* ══════════════════
   HERO BANNER
══════════════════ */
function HeroBanner({ products = [] }) {
  const [current,       setCurrent]   = useState(0);
  const [prev,          setPrev]      = useState(null);
  const [direction,     setDirection] = useState('next');
  const [transitioning, setTrans]     = useState(false);
  const timerRef = useRef(null);
  

  const go = useCallback((idx, dir = 'next') => {
    if (transitioning) return;
    setTrans(true);
    setDirection(dir);
    setPrev(current);
    setCurrent(idx);
    setTimeout(() => { setPrev(null); setTrans(false); }, 1050);
  }, [current, transitioning]);

  const goNext = useCallback(() => go((current + 1) % bannerSlides.length, 'next'), [go, current]);
  const goPrev = useCallback(() => go((current - 1 + bannerSlides.length) % bannerSlides.length, 'prev'), [go, current]);

  useEffect(() => {
    timerRef.current = setInterval(goNext, 5500);
    return () => clearInterval(timerRef.current);
  }, [goNext]);

  const pause  = () => clearInterval(timerRef.current);
  const resume = () => { timerRef.current = setInterval(goNext, 5500); };

  const slide = bannerSlides[current];

  // NEW: find the matching real product so we can link to its detail page
  const matchedProduct = products.find(
    p => p.name?.trim().toLowerCase() === slide.title.trim().toLowerCase()
  );
  const resolvedCtaPath = matchedProduct?.id
    ? `/products/${matchedProduct.id}`
    : slide.ctaPath;

  return (
    <section className="hb-section" onMouseEnter={pause} onMouseLeave={resume}>
      <div className="hb-image-wrap">
        {prev !== null && (
          <img key={`prev-${prev}`} src={bannerSlides[prev].image} alt={bannerSlides[prev].title} className="hb-img hb-img-out" />
        )}
        <img key={`cur-${current}`} src={slide.image} alt={slide.title} className="hb-img hb-img-in" />
        <div className="hb-scrim" />
        <div className="hb-overlay-content">
          <div key={`text-${current}`} className={`hb-text-block hb-text-${direction}`}>
            <h1 className="hb-title">{slide.title}</h1>
            <h2 className="hb-highlight">{slide.highlight}</h2>
            <p className="hb-desc">{slide.desc}</p>
            <div className="hb-cta-row">
              <Link
                to={resolvedCtaPath} className="hb-cta-primary"
                style={{ background: slide.accent, boxShadow: `0 8px 28px ${slide.accent}55` }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 14px 40px ${slide.accent}66`; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=`0 8px 28px ${slide.accent}55`; }}
              >
                {slide.cta} <ArrowRight size={15} />
              </Link>
              <Link
                to="/products" className="hb-cta-ghost"
                onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.22)'}
                onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.10)'}
              >
                All Products
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="hb-controls">
        <button className="hb-arrow" onClick={goPrev}
          onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
          onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.12)'}>
          <ChevronLeft size={16} />
        </button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {bannerSlides.map((s, i) => (
            <button key={i} onClick={() => go(i, i > current ? 'next' : 'prev')} style={{
              width: i === current ? 32 : 10, height: 10, borderRadius: 99,
              padding: 0, border: 'none', cursor: 'pointer',
              background: i === current ? slide.accent : 'rgba(46,180,180,0.3)',
              boxShadow: i === current ? `0 0 12px ${slide.accent}88` : 'none',
              transition: 'all .4s cubic-bezier(.22,1,.36,1)',
            }} />
          ))}
        </div>
        <button className="hb-arrow" onClick={goNext}
          onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
          onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.12)'}>
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="hb-counter">
        <span style={{ color: '#fff', fontWeight: 900 }}>{String(current + 1).padStart(2, '0')}</span>
        {' / '}{String(bannerSlides.length).padStart(2, '0')}
      </div>

      <style>{`
        .hb-section { position: relative; width: 100%; background: #0a1628; overflow: hidden; }
        .hb-image-wrap { position: relative; width: 100%; height: 88vh; min-height: 480px; max-height: 860px; }
        .hb-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center top; user-select: none; pointer-events: none; opacity: 0.82; transform: translate3d(0,0,0) scale(1); backface-visibility: hidden; will-change: opacity, transform, filter; }
        .hb-img-in  { z-index: 1; animation: hb-bg-in  1s cubic-bezier(.19,1,.22,1) both; }
        .hb-img-out { z-index: 2; animation: hb-bg-out 1s cubic-bezier(.4,0,.2,1) both; }
        .hb-scrim {
          position: absolute;
          inset: 0;
          z-index: 3;
          pointer-events: none;
          background: linear-gradient(
            to right,
            rgba(10, 22, 40, 0.40) 0%,
            rgba(10, 22, 40, 0.30) 35%,
            rgba(10, 22, 40, 0.20) 65%,
            rgba(10, 22, 40, 0.10) 100%
          );
        }
        .hb-overlay-content { position: absolute; inset: 0; z-index: 4; display: flex; align-items: center; padding: 72px 48px; max-width: 1360px; margin: 0 auto; left: 50%; transform: translateX(-50%); width: 100%; box-sizing: border-box; }
        .hb-text-block { max-width: 520px; width: 100%; }
        .hb-title { font-size: clamp(2.4rem, 4.5vw, 5rem); font-weight: 900; color: #fff; line-height: 1.05; letter-spacing: -0.03em; margin: 0; text-shadow: 0 2px 24px rgba(0,0,0,0.4); }
        .hb-highlight { font-size: clamp(1.1rem, 2vw, 2rem); font-weight: 700; color: rgba(255,255,255,1); letter-spacing: -0.02em; margin: 10px 0 16px; }
        .hb-desc { color: rgba(255,255,255,0.95); font-size: 15px; line-height: 1.8; max-width: 400px; margin-bottom: 28px; text-shadow: 0 1px 8px rgba(0,0,0,0.3); }
        .hb-cta-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
        .hb-cta-primary { color: #fff; font-weight: 700; padding: 12px 24px; border-radius: 12px; display: inline-flex; align-items: center; gap: 8px; font-size: 14px; text-decoration: none; transition: transform .35s cubic-bezier(.19,1,.22,1), box-shadow .35s cubic-bezier(.19,1,.22,1), background .35s ease; white-space: nowrap; flex-shrink: 0; }
        .hb-cta-ghost { background: rgba(255,255,255,0.10); color: rgba(255,255,255,0.9); font-weight: 600; padding: 12px 24px; border-radius: 12px; display: inline-flex; align-items: center; gap: 8px; font-size: 14px; text-decoration: none; border: 1px solid rgba(255,255,255,0.22); backdrop-filter: blur(8px); transition: background .35s ease, border-color .35s ease, color .35s ease, transform .35s cubic-bezier(.19,1,.22,1); white-space: nowrap; flex-shrink: 0; }
        .hb-controls { position: absolute; bottom: 0; left: 0; right: 0; z-index: 10; display: flex; align-items: center; justify-content: center; padding: 16px 24px; background: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%); }
        .hb-arrow { width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.22); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .3s ease, border-color .3s ease, transform .3s cubic-bezier(.19,1,.22,1); flex-shrink: 0; }
        .hb-controls .hb-arrow:first-child { margin-right: 14px; }
        .hb-controls .hb-arrow:last-child  { margin-left: 14px; }
        .hb-counter { position: absolute; top: 16px; right: 18px; z-index: 10; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; color: rgba(255,255,255,0.6); background: rgba(0,0,0,0.35); padding: 4px 10px; border-radius: 20px; backdrop-filter: blur(6px); pointer-events: none; }
        .hb-text-next { animation: hb-text-in-next 0.85s cubic-bezier(.19,1,.22,1) both; }
        .hb-text-prev { animation: hb-text-in-prev 0.85s cubic-bezier(.19,1,.22,1) both; }
        @keyframes hb-bg-in  { from { opacity:0; transform:translate3d(0,0,0) scale(1.035); filter:blur(3px); } to { opacity:.82; transform:translate3d(0,0,0) scale(1); filter:blur(0); } }
        @keyframes hb-bg-out { from { opacity:.82; transform:translate3d(0,0,0) scale(1); filter:blur(0); } to { opacity:0; transform:translate3d(0,0,0) scale(1.015); filter:blur(2px); } }
        @keyframes hb-text-in-next { from { opacity:0; transform:translate3d(0,26px,0); filter:blur(5px); } to { opacity:1; transform:translate3d(0,0,0); filter:blur(0); } }
        @keyframes hb-text-in-prev { from { opacity:0; transform:translate3d(0,-26px,0); filter:blur(5px); } to { opacity:1; transform:translate3d(0,0,0); filter:blur(0); } }
        @media (max-width: 1024px) { .hb-image-wrap { height: 70vh; min-height: 400px; } }
        @media (max-width: 768px) {
          .hb-image-wrap { height: 72vw; min-height: 300px; max-height: 480px; }
          .hb-img { object-fit: cover; object-position: center center; }
          .hb-scrim { background: linear-gradient(to top, rgba(10,22,40,0.92) 0%, rgba(10,22,40,0.60) 50%, rgba(10,22,40,0.10) 100%) !important; }
          .hb-overlay-content { align-items: flex-end; padding: 20px 18px 54px; left: 0; transform: none; }
          .hb-text-block { max-width: 100%; }
          .hb-title { font-size: 1.65rem; }
          .hb-highlight { font-size: 1rem; margin-bottom: 8px; }
          .hb-desc { font-size: 13px; margin-bottom: 18px; max-width: 100%; }
          .hb-cta-row { gap: 8px; }
          .hb-cta-primary, .hb-cta-ghost { padding: 9px 16px; font-size: 13px; border-radius: 10px; }
          .hb-counter { top: 10px; right: 12px; font-size: 11px; padding: 3px 8px; }
          .hb-controls { padding: 10px 14px; }
          .hb-arrow { width: 30px; height: 30px; }
          .hb-controls .hb-arrow:first-child { margin-right: 10px; }
          .hb-controls .hb-arrow:last-child { margin-left: 10px; }
        }
        @media (max-width: 480px) {
          .hb-image-wrap { height: 80vw; min-height: 260px; max-height: 380px; }
          .hb-title { font-size: 1.4rem; }
          .hb-highlight { font-size: 0.9rem; }
          .hb-desc { display: none; }
          .hb-overlay-content { padding: 14px 14px 50px; }
        }
      `}</style>
    </section>
  );
}

/* ══════════════════
   PRODUCT CARD (Enhanced)
══════════════════ */
function ProductCard({ product, index }) {
  const [hovered, setHovered] = useState(false);
  const accent = product.accent || TEAL;

  return (
    <Reveal delay={index * 80} direction="up">
      <div
        className="product-card"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: '#fff',
          borderRadius: 22,
          border: `1.5px solid ${hovered ? accent + '40' : '#eef2f8'}`,
          overflow: 'hidden',
          transition: 'all .35s cubic-bezier(.22,1,.36,1)',
          transform: hovered ? 'translateY(-10px)' : 'translateY(0)',
          boxShadow: hovered ? `0 28px 60px rgba(23,79,154,0.16), 0 0 0 1px ${accent}22` : '0 2px 12px rgba(23,79,154,0.05)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {/* Image / Gradient area */}
        <div style={{ position: 'relative', paddingTop: '62%', overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(180deg,#ffffff 0%,#f8fbfc 100%)' }}>
          {/* Gradient BG */}
          {/* <div className={`bg-gradient-to-br ${product.color}`} style={{
            position: 'absolute', inset: 0,
            transition: 'transform .5s cubic-bezier(.22,1,.36,1)',
            transform: hovered ? 'scale(1.06)' : 'scale(1)',
          }} /> */}

          {/* Product image */}
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                opacity: hovered ? 1 : 0.95,
                transition: 'all .35s ease',
                transform: hovered ? 'scale(1.025)' : 'scale(1)',
              }}
            />
          )}

          {/* Category badge — top left */}
          <div style={{
            position: 'absolute', top: 14, left: 14,
            background: product.tagBg || 'rgba(46,180,180,0.18)',
            border: `1px solid ${accent}44`,
            color: product.tagColor || accent,
            padding: '4px 12px', borderRadius: 99,
            fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            backdropFilter: 'blur(8px)',
          }}>
            {product.category}
          </div>
        </div>

        {/* Product name row — clean, below image */}
        <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0d1f3c', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              {product.name}
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginTop: 3 }}>
              {product.type}
            </div>
          </div>
        </div>

        {/* Card body */}
        <div style={{
          padding: '12px 20px 20px',
          display: 'flex', flexDirection: 'column', gap: 14,
          flex: 1,
        }}>
          {/* Description */}
          <p style={{
            fontSize: 13.5, color: '#6b7280', lineHeight: 1.65,
            margin: 0, flex: 1,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {product.desc}
          </p>

          {/* Divider */}
          <div style={{ height: 1, background: '#f0f4f8' }} />

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: '#9ca3af', fontWeight: 600,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: accent,
                boxShadow: `0 0 0 3px ${accent}22`,
              }} />
              Available
            </div>
            <Link
              to={product.id ? `/products/${product.id}` : '/products'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 700,
                color: accent, textDecoration: 'none',
                padding: '7px 14px', borderRadius: 10,
                background: `${accent}10`,
                border: `1px solid ${accent}28`,
                transition: 'all .22s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${accent}10`; e.currentTarget.style.color = accent; e.currentTarget.style.borderColor = `${accent}28`; }}
            >
              View Details <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

/* ══ Main Page ══ */
export default function Home() {
  const [backendProducts, setBackend] = useState([]);
  const [statRef, statVis] = useInView(0.3);
  const [netRef,  netVis]  = useInView(0.2);

  useEffect(() => {
    api.products({ featured: true, page_size: 6 })
      .then(d => setBackend(d.results || []))
      .catch(() => setBackend([]));
  }, []);

  const displayProducts = backendProducts.map((p, i) => {
    const style = featuredProductStyles[i % featuredProductStyles.length];
    return {
        id: p.id, name: p.name,
        type: p.formulation || p.packing_size || p.product_category_name || 'Featured',
        category: p.product_category_name || p.division_name || 'Product',
        image: p.images?.[0]?.url,
        color: style.color,
        accent: style.accent,
        tagBg: style.tagBg,
        tagColor: style.tagColor,
        desc: p.description,
      };
    });

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", overflowX: 'hidden' }}>

      {/* ══ HERO ══ */}
      <HeroBanner />

      {/* ══ STATS ══ */}
      <section ref={statRef} style={{ background: '#fff', borderBottom: '1px solid #f0f4f8', boxShadow: '0 2px 24px rgba(23,79,154,0.05)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
          <div className="stats-grid">
            {stats.map(({ value, label, icon: Icon }, i) => (
              <div key={label} className="stat-item" style={{
                padding: '36px 20px',
                borderRight: i < stats.length - 1 ? '1px solid #f0f4f8' : 'none',
                transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s cubic-bezier(.22,1,.36,1) ${i * 120}ms`,
                opacity: statVis ? 1 : 0,
                transform: statVis ? 'translateY(0)' : 'translateY(24px)',
                textAlign: 'center',
              }}>
                <div style={{
                  width: 52, height: 52, ...grad, borderRadius: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 14px', boxShadow: `0 8px 24px rgba(46,180,180,0.28)`,
                  transition: 'transform .3s',
                }}
                  onMouseEnter={e => e.currentTarget.style.transform='scale(1.1) rotate(-4deg)'}
                  onMouseLeave={e => e.currentTarget.style.transform=''}>
                  <Icon size={22} color="#fff" />
                </div>
                <div style={{ fontSize: 30, fontWeight: 900, ...gradT, marginBottom: 5, lineHeight: 1 }}>
                  <CountUp end={value} trigger={statVis} />
                </div>
                <div style={{ fontSize: 11, color: '#7a8a9a', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ABOUT ══ */}
      <section style={{ padding: 'clamp(56px,8vw,96px) 24px', background: '#f7fafd' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }} className="about-grid">
          <Reveal direction="right">
            <div style={{ position: 'relative' }}>
              <div style={{
                ...grad, borderRadius: 28, padding: 'clamp(28px,4vw,44px) clamp(24px,3vw,40px)', color: '#fff',
                boxShadow: '0 28px 72px rgba(23,79,154,0.28)', position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', right: 20, top: 16, fontSize: 88, fontWeight: 900, opacity: 0.07, lineHeight: 1, userSelect: 'none', letterSpacing: -6 }}>GHC</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>Galaxy Health Care</h3>
                <p style={{ color: 'rgba(255,255,255,0.77)', lineHeight: 1.7, fontSize: 15, marginBottom: 28 }}>
                  Bridging the gap between quality pharmaceutical products and every household in India.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {['WHO-GMP Certified', 'GST Compliant', 'Drug Licensed', 'Quality Assured'].map((b) => (
                    <div key={b} style={{
                      background: 'rgba(255,255,255,0.12)', borderRadius: 10,
                      padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 12, fontWeight: 700,
                    }}>
                      <CheckCircle size={13} /> {b}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{
                position: 'absolute', bottom: -20, right: -16,
                width: 80, height: 80, background: '#fff', borderRadius: 20,
                boxShadow: '0 8px 32px rgba(23,79,154,0.16)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid #e8f0fa',
                animation: 'float-badge 4s ease-in-out infinite',
              }}>
                <span style={{ fontSize: 20, fontWeight: 900, ...gradT }}>15+</span>
                <span style={{ fontSize: 11, color: '#7a8a9a', fontWeight: 600 }}>Years</span>
              </div>
            </div>
          </Reveal>
          <Reveal direction="left" delay={120}>
            <p style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', ...gradT, marginBottom: 10 }}>Who We Are</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 900, color: '#0d1f3c', lineHeight: 1.12, letterSpacing: '-0.02em', marginBottom: 20 }}>
              Your Trusted<br />Pharmaceutical Partner
            </h2>
            <p style={{ color: '#4a5568', fontSize: 16, lineHeight: 1.75, marginBottom: 14 }}>
              We are a pharmaceutical marketing company committed to delivering high-quality, safe, and affordable healthcare products to everyone at the earliest.
            </p>
            <p style={{ color: '#6b7280', fontSize: 15, lineHeight: 1.75, marginBottom: 28 }}>
              Our portfolio includes tablets, capsules, syrups, cosmetics, and other healthcare products — driven by ethical practices and continuous growth.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
              {[
                'Vision: Quality medicine for everyone at affordable prices',
                'Mission: Reach cities and rural areas with reliable supply chains',
                'Values: Ethics, Trust, Quality, and Innovation',
              ].map((item) => (
                <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <CheckCircle size={18} color={TEAL} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 15, color: '#374151', fontWeight: 600 }}>{item}</span>
                </div>
              ))}
            </div>
            <Link to="/company" style={btnTeal}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 16px 44px rgba(46,180,180,0.42)`; }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=`0 8px 30px rgba(46,180,180,0.30)`; }}>
              Learn More <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ══ FEATURED PRODUCTS — ENHANCED ══ */}
      <section style={{ padding: 'clamp(56px,8vw,96px) 24px', background: '#fff', position: 'relative', overflow: 'hidden' }}>
        {/* Subtle background pattern */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `radial-gradient(circle at 80% 20%, rgba(46,180,180,0.05) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(23,79,154,0.04) 0%, transparent 50%)`,
        }} />

        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>

          {/* Section header */}
          <Reveal>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20, marginBottom: 52 }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, ...grad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Sparkles size={14} color="#fff" />
                  </div>
                  <p style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', ...gradT, margin: 0 }}>Our Portfolio</p>
                </div>
                <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 900, color: '#0d1f3c', marginBottom: 12, letterSpacing: '-0.02em', margin: 0 }}>
                  Featured Products
                </h2>
                <p style={{ color: '#6b7280', maxWidth: 440, lineHeight: 1.7, fontSize: 15, marginTop: 12, marginBottom: 0 }}>
                  From healthcare to cosmetics — premium quality formulations crafted for every need.
                </p>
              </div>
              <Link to="/products" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 14, fontWeight: 700, color: TEAL, textDecoration: 'none',
                padding: '10px 20px', borderRadius: 12,
                border: `1.5px solid ${TEAL}33`,
                background: `${TEAL}08`,
                transition: 'all .22s',
                flexShrink: 0,
              }}
                onMouseEnter={e => { e.currentTarget.style.background = TEAL; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = TEAL; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${TEAL}08`; e.currentTarget.style.color = TEAL; e.currentTarget.style.borderColor = `${TEAL}33`; }}>
                See All Products <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>

          {/* Product grid */}
          <div className="products-grid">
            {displayProducts.map((product, i) => (
              <ProductCard key={product.id || product.name} product={product} index={i} />
            ))}
          </div>

          {/* Bottom CTA */}
          <Reveal delay={200}>
            <div style={{
              marginTop: 56,
              padding: 'clamp(28px,4vw,40px) clamp(24px,4vw,48px)',
              borderRadius: 24,
              ...grad,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 20,
              boxShadow: `0 20px 60px rgba(46,180,180,0.25)`,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', right: -20, top: -20, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', right: 60, bottom: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h3 style={{ fontSize: 'clamp(1.2rem,2.5vw,1.6rem)', fontWeight: 900, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
                  Explore Our Complete Range
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                  500+ products across healthcare, cosmetics, nutraceuticals & more.
                </p>
              </div>
              <Link to="/products" style={{
                position: 'relative', zIndex: 1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#fff', color: NAVY, fontWeight: 800,
                padding: '13px 26px', borderRadius: 14, fontSize: 14,
                textDecoration: 'none', flexShrink: 0,
                boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                transition: 'all .22s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 14px 40px rgba(0,0,0,0.24)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 8px 28px rgba(0,0,0,0.18)'; }}>
                View All Products <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ WHY US ══ */}
      <section style={{ padding: 'clamp(56px,8vw,96px) 24px', background: '#0d1f3c', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle, rgba(46,180,180,0.18) 1.5px, transparent 1.5px)`, backgroundSize: '40px 40px', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <p style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: TEAL, marginBottom: 10 }}>Our Strengths</p>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 900, color: '#fff', marginBottom: 14, letterSpacing: '-0.02em' }}>Why Choose Us?</h2>
              <p style={{ color: 'rgba(255,255,255,0.45)', maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
                Six core pillars that make us the preferred pharmaceutical partner across India.
              </p>
            </div>
          </Reveal>
          <div className="why-grid">
            {whyUs.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 90} direction="up">
                <div style={{ padding: '24px', borderRadius: 18, border: '1.5px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)', transition: 'all .3s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(46,180,180,0.45)'; e.currentTarget.style.background='rgba(46,180,180,0.08)'; e.currentTarget.style.transform='translateY(-5px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'; e.currentTarget.style.background='rgba(255,255,255,0.03)'; e.currentTarget.style.transform=''; }}>
                  <div style={{ width: 48, height: 48, ...grad, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: `0 6px 20px rgba(46,180,180,0.28)`, transition: 'transform .25s' }}
                    onMouseEnter={e => e.currentTarget.style.transform='rotate(-8deg) scale(1.1)'}
                    onMouseLeave={e => e.currentTarget.style.transform=''}>
                    <Icon size={20} color="#fff" />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 8 }}>{title}</h3>
                  <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.48)', lineHeight: 1.7 }}>{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ NETWORK ══ */}
      <section ref={netRef} style={{ padding: 'clamp(56px,8vw,96px) 24px', background: '#f7fafd' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }} className="network-grid">
          <Reveal direction="right">
            <p style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', ...gradT, marginBottom: 10 }}>Our Reach</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 900, color: '#0d1f3c', lineHeight: 1.12, letterSpacing: '-0.02em', marginBottom: 16 }}>
              Network Coverage<br />Across India
            </h2>
            <p style={{ color: '#4a5568', fontSize: 15, lineHeight: 1.75, marginBottom: 32 }}>
              From metro cities to remote villages, our distribution network ensures that quality medicines reach everyone who needs them.
            </p>
            {[
              { label: 'Urban Distribution', pct: 95 },
              { label: 'Semi-Urban Reach',   pct: 80 },
              { label: 'Rural Coverage',     pct: 65 },
            ].map(({ label, pct }, i) => (
              <div key={label} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>{label}</span>
                  <span style={{ fontSize: 14, fontWeight: 800, ...gradT }}>{pct}%</span>
                </div>
                <AnimatedBar pct={pct} trigger={netVis} delay={i * 180} />
              </div>
            ))}
            <Link to="/responsibility" style={{ ...btnTeal, marginTop: 24, display: 'inline-flex' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 16px 44px rgba(46,180,180,0.42)`; }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=`0 8px 30px rgba(46,180,180,0.30)`; }}>
              Our Responsibility <ArrowRight size={17} />
            </Link>
          </Reveal>
          <Reveal direction="left" delay={140}>
            <div className="network-cards">
              {[
                { label: 'Hospitals Served', value: '200+', bg: '#f0faf9', accent: TEAL       },
                { label: 'Pharmacies',        value: '500+', bg: '#eff5ff', accent: NAVY       },
                { label: 'Distributors',      value: '100+', bg: '#f5f0ff', accent: '#7c3aed' },
                { label: 'States',            value: '20+',  bg: '#fff7ed', accent: '#ea580c' },
              ].map(({ label, value, bg, accent }) => (
                <div key={label} style={{ background: bg, borderRadius: 20, padding: 'clamp(20px,3vw,28px) clamp(18px,2.5vw,24px)', border: `1.5px solid ${accent}18`, transition: 'all .3s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-5px)'; e.currentTarget.style.boxShadow=`0 14px 36px ${accent}28`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                  <div style={{ fontSize: 'clamp(26px,4vw,34px)', fontWeight: 900, color: accent, marginBottom: 6 }}>{value}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ Global styles ══ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&display=swap');

        @keyframes float-badge {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }

        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(32px,5vw,72px); align-items: center; }
        .products-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .why-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
        .network-grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(32px,5vw,72px); align-items: center; }
        .network-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

        .product-card { outline: none; }

        @media (max-width: 1024px) {
          .about-grid, .network-grid { grid-template-columns: 1fr !important; }
          .why-grid { grid-template-columns: repeat(2,1fr) !important; }
          .products-grid { grid-template-columns: repeat(2,1fr) !important; }
          .network-cards { grid-template-columns: repeat(4,1fr) !important; }
        }
        @media (max-width: 640px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .stats-grid > div { border-right: none !important; border-bottom: 1px solid #f0f4f8; }
          .stats-grid > div:nth-child(odd) { border-right: 1px solid #f0f4f8 !important; }
          .stats-grid > div:nth-last-child(-n+2) { border-bottom: none !important; }
          .why-grid { grid-template-columns: 1fr !important; }
          .products-grid { grid-template-columns: 1fr !important; }
          .network-cards { grid-template-columns: repeat(2,1fr) !important; }
          .about-grid, .network-grid { gap: 32px !important; }
        }
        @media (max-width: 400px) {
          .network-cards { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}
